import { useState, useEffect, useCallback } from 'react';
import { fetchQuota, type QuotaResponse } from '../api/client';

interface QuotaWidgetProps {
  className?: string;
  refreshInterval?: number; // Interval in ms, default 30000
}

/**
 * Get color classes based on alert level
 */
function getStatusColors(alertLevel: 'ok' | 'warning' | 'critical') {
  switch (alertLevel) {
    case 'critical':
      return {
        text: 'text-danger',
        bg: 'bg-danger',
        badge: 'bg-danger/20 text-danger border-danger/30',
      };
    case 'warning':
      return {
        text: 'text-warning',
        bg: 'bg-warning',
        badge: 'bg-warning/20 text-warning border-warning/30',
      };
    default:
      return {
        text: 'text-success',
        bg: 'bg-success',
        badge: 'bg-success/20 text-success border-success/30',
      };
  }
}

/**
 * Format relative time from ISO timestamp
 */
function formatRelativeTime(isoString: string): string {
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return 'just now';
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
}

/**
 * Format days remaining estimate
 */
function formatDaysRemaining(days: number | null): string {
  if (days === null) return 'N/A';
  if (days === 0) return '<1 day';
  if (days === 1) return '1 day';
  return `${days} days`;
}

/**
 * Estimate hours until quota exhaustion based on burn rate
 */
function estimateHoursRemaining(remaining: number, burnRate: number): string {
  if (burnRate <= 0) return 'N/A';
  const hours = remaining / burnRate;
  if (hours > 24) return `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h`;
  if (hours > 1) return `${hours.toFixed(1)}h`;
  return `${Math.floor(hours * 60)}m`;
}

export default function QuotaWidget({ className = '', refreshInterval = 30000 }: QuotaWidgetProps) {
  const [stats, setStats] = useState<QuotaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadQuota = useCallback(async () => {
    try {
      const data = await fetchQuota();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('[QuotaWidget] Failed to fetch quota:', err);
      setError('Failed to load quota');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load immediately
    loadQuota();

    // Set up periodic refresh
    const interval = setInterval(loadQuota, refreshInterval);
    return () => clearInterval(interval);
  }, [loadQuota, refreshInterval]);

  if (loading && !stats) {
    return (
      <div className={`card ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 w-24 bg-surface-50 rounded mb-3"></div>
          <div className="h-8 w-16 bg-surface-50 rounded mb-2"></div>
          <div className="h-2 bg-surface-50 rounded"></div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className={`card ${className}`}>
        <div className="text-sm text-gray-400">API Quota</div>
        <div className="text-danger text-sm mt-2">{error}</div>
        <button
          onClick={loadQuota}
          className="mt-2 text-xs text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const colors = getStatusColors(stats.alertLevel);
  const dailyLimit = 500; // Standard API daily limit
  const usagePercent = Math.min((stats.usedToday / dailyLimit) * 100, 100);

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">API Quota</span>
          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${colors.badge}`}>
            {stats.alertLevel === 'critical' ? 'CRITICAL' : stats.alertLevel === 'warning' ? 'LOW' : 'OK'}
          </span>
        </div>
        <span className="text-xs text-gray-500">{formatRelativeTime(stats.lastUpdated)}</span>
      </div>

      {/* Main Metric - Remaining Credits */}
      <div className="mb-4">
        <div className={`text-3xl font-bold ${colors.text}`}>
          {stats.remaining.toLocaleString()}
        </div>
        <div className="text-sm text-gray-400">credits remaining</div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>Used today: {stats.usedToday}</span>
          <span>{usagePercent.toFixed(0)}%</span>
        </div>
        <div className="progress-bar">
          <div
            className={`progress-fill ${
              usagePercent > 90 ? 'bg-danger' :
              usagePercent > 70 ? 'bg-warning' :
              'bg-success'
            }`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-surface-50 rounded-lg p-2">
          <div className="text-gray-400 text-xs mb-1">Burn Rate</div>
          <div className="font-medium">{stats.burnRate.toFixed(1)}/hr</div>
        </div>
        <div className="bg-surface-50 rounded-lg p-2">
          <div className="text-gray-400 text-xs mb-1">Est. Time Left</div>
          <div className={`font-medium ${stats.alertLevel === 'critical' ? 'text-danger' : ''}`}>
            {estimateHoursRemaining(stats.remaining, stats.burnRate)}
          </div>
        </div>
        <div className="bg-surface-50 rounded-lg p-2">
          <div className="text-gray-400 text-xs mb-1">This Hour</div>
          <div className="font-medium">{stats.usedThisHour}</div>
        </div>
        <div className="bg-surface-50 rounded-lg p-2">
          <div className="text-gray-400 text-xs mb-1">Days Left</div>
          <div className={`font-medium ${stats.projectedDaysRemaining !== null && stats.projectedDaysRemaining < 3 ? 'text-warning' : ''}`}>
            {formatDaysRemaining(stats.projectedDaysRemaining)}
          </div>
        </div>
      </div>

      {/* Warning Messages */}
      {stats.alertLevel === 'critical' && (
        <div className="mt-3 p-2 bg-danger/10 border border-danger/30 rounded-lg text-xs text-danger">
          Quota critically low! Polling has been automatically reduced to urgent events only.
        </div>
      )}
      {stats.alertLevel === 'warning' && (
        <div className="mt-3 p-2 bg-warning/10 border border-warning/30 rounded-lg text-xs text-warning">
          Quota running low. Polling intervals are being extended to conserve credits.
        </div>
      )}
    </div>
  );
}

/**
 * Compact version of the quota widget for use in headers or sidebars
 */
export function QuotaWidgetCompact({ className = '', refreshInterval = 30000 }: QuotaWidgetProps) {
  const [stats, setStats] = useState<QuotaResponse | null>(null);

  const loadQuota = useCallback(async () => {
    try {
      const data = await fetchQuota();
      setStats(data);
    } catch (err) {
      console.error('[QuotaWidgetCompact] Failed to fetch quota:', err);
    }
  }, []);

  useEffect(() => {
    loadQuota();
    const interval = setInterval(loadQuota, refreshInterval);
    return () => clearInterval(interval);
  }, [loadQuota, refreshInterval]);

  if (!stats) {
    return <div className="animate-pulse h-6 w-24 bg-surface-50 rounded"></div>;
  }

  const colors = getStatusColors(stats.alertLevel);

  return (
    <div className={`flex items-center gap-2 ${className}`} title={`API Quota: ${stats.remaining} remaining`}>
      <div className={`w-2 h-2 rounded-full ${colors.bg}`}></div>
      <span className={`text-sm font-medium ${colors.text}`}>
        {stats.remaining.toLocaleString()}
      </span>
      <span className="text-xs text-gray-500">credits</span>
    </div>
  );
}
